/**
 * Texto e agrupamento da confirmação de agenda (PLANO 8.1) — Fase 2.
 *
 * Puro de propósito: nada aqui envia, toca rede ou lê Firestore. É o que
 * permite provar as duas regras que doem se saírem erradas — uma mensagem por
 * PESSOA e o horário mais cedo — sem disparar nada para paciente nenhum.
 */

/** Variáveis oferecidas como chips no editor. */
export const VARIAVEIS = [
  { chave: '{nome}', descricao: 'primeiro nome do paciente' },
  { chave: '{nome_completo}', descricao: 'nome como está na agenda' },
  { chave: '{dia}', descricao: 'dia da consulta (terça-feira, 25/08)' },
  { chave: '{horario}', descricao: 'horário mais cedo do dia' },
  { chave: '{telefone}', descricao: 'celular do paciente' },
];

/** Os dois momentos do fluxo. A ordem é a que aparece no seletor. */
/**
 * Os modelos de texto de cada modo da tela.
 *
 * Confirmação tem três, que são o mesmo público (EM ABERTO) em três alturas do
 * fluxo: a primeira, a insistência e o cancelamento de quem nunca respondeu.
 * Reagendamento tem um só — não existe "reconfirmar um reagendamento", porque a
 * pessoa já faltou e o que se quer é uma resposta, não uma cobrança repetida.
 */
export const MODELOS_POR_MODO = {
  confirmacao: [
    {
      id: 'primeira',
      rotulo: 'Primeira confirmação (2 dias antes)',
      campo: 'primeira',
    },
    {
      id: 'reconfirmacao',
      rotulo: 'Reconfirmação (quem não respondeu)',
      campo: 'reconfirmacao',
    },
    {
      id: 'cancelamento',
      rotulo: 'Cancelamento com reagendamento (não respondeu)',
      campo: 'cancelamento',
    },
  ],
  reagendamento: [
    {
      id: 'reagendamento',
      rotulo: 'Reagendamento (quem faltou)',
      campo: 'reagendamento',
    },
  ],
};

/** Os modelos do modo, com a confirmação como reserva para modo desconhecido. */
export const modelosDoModo = (modo) =>
  MODELOS_POR_MODO[modo] ?? MODELOS_POR_MODO.confirmacao;

/** Compatibilidade: era esta a lista antes de existirem modos. */
export const MODELOS = MODELOS_POR_MODO.confirmacao;

export const TEXTOS_PADRAO = {
  primeira:
    'Olá {nome}, aqui é da recepção da Sorridents. Passando para confirmar sua ' +
    'consulta no dia {dia} às {horario}. Está confirmado? Se precisar remarcar, ' +
    'é só responder por aqui. 😊',
  reconfirmacao:
    'Oi {nome}! Ainda não recebi sua confirmação da consulta do dia {dia} às ' +
    '{horario}. Consigo confirmar com você? 🙏',
  /**
   * O fim da linha de quem continuou EM ABERTO depois das duas primeiras: a
   * vaga é liberada, mas a mensagem já abre a porta do reagendamento — cancelar
   * sem oferecer volta é o que faz o paciente sumir de vez.
   */
  cancelamento:
    'Olá {nome}, como não recebemos a confirmação da sua consulta do dia ' +
    '{dia} às {horario}, precisamos cancelar esse agendamento. Mas ficamos à ' +
    'disposição para reagendar no melhor horário para você — é só nos enviar ' +
    'por aqui a sua disponibilidade. 😊',
  /**
   * ⚠ Sem `{horario}` de propósito: a consulta que a pessoa perdeu já passou, e
   * repetir a hora dela soaria como cobrança. O `{dia}` fica porque situa de
   * qual falta se está falando.
   */
  reagendamento:
    'Oi {nome}, aqui é da recepção da Sorridents. Vi que não deu para você ' +
    'comparecer à consulta do dia {dia}. Quer que eu já reagende um novo horário ' +
    'para você? É só me responder por aqui. 😊',
};

/**
 * Primeiro nome, como a recepção falaria.
 *
 * "MARIA APARECIDA DA SILVA" vira "Maria", não "MARIA": a agenda do Careflow
 * vem toda em caixa alta, e mandar o nome gritado numa mensagem de WhatsApp
 * soa como cobrança automática.
 */
export function primeiroNome(nomeCompleto) {
  const primeiro = String(nomeCompleto ?? '').trim().split(/\s+/)[0] ?? '';
  if (!primeiro) return '';
  return primeiro.charAt(0).toUpperCase() + primeiro.slice(1).toLowerCase();
}

/** Nome inteiro com iniciais maiúsculas, pelo mesmo motivo do `primeiroNome`. */
export function nomeApresentavel(nomeCompleto) {
  return String(nomeCompleto ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((p) =>
      // Preposições ficam minúsculas: "MARIA DA SILVA" -> "Maria da Silva".
      /^(DA|DE|DO|DAS|DOS|E)$/i.test(p)
        ? p.toLowerCase()
        : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase(),
    )
    .join(' ');
}

/**
 * `2026-08-25` → `terça-feira, 25/08`. Vazio se a data não vier ou não existir.
 *
 * O dia da semana entra porque é assim que o paciente confere: ele lembra que
 * marcou "na terça", não que marcou "no 25". Sem ele, um erro de data no lote
 * passaria despercebido pelos dois lados.
 *
 * A data é montada pelos NÚMEROS (ano, mês-1, dia), nunca por `new Date(iso)`:
 * a string `2026-08-25` é lida como UTC, e num fuso a oeste de Greenwich isso
 * volta um dia — o paciente receberia "segunda-feira, 24/08".
 *
 * Sem ano de propósito: a agenda é sempre da semana corrente, e o ano só
 * alongaria a mensagem.
 */
export function formatarDia(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ''));
  if (!m) return '';

  const [, ano, mes, dia] = m.map(Number);
  const data = new Date(ano, mes - 1, dia);

  // Data que não existe (31/02) NÃO é rejeitada pelo construtor: ela rola para
  // o mês seguinte e devolveria um dia da semana errado com toda a confiança.
  // A volta pelos componentes é o que pega isso.
  if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) {
    return '';
  }

  const semana = data.toLocaleDateString('pt-BR', { weekday: 'long' }).toLowerCase();
  return `${semana}, ${m[3]}/${m[2]}`;
}

/**
 * Agrupa as consultas MARCADAS por pessoa.
 *
 * O telefone é a identidade, não o nome: a mesma pessoa aparece grafada de duas
 * formas entre registros, e o celular já vem canônico do `agendaParser` (com o
 * nono dígito), que é a mesma chave usada para deduplicar conversas.
 *
 * Uma pessoa com três consultas no dia recebe UMA mensagem. Três mensagens
 * seguidas do mesmo número parecem robô, e são o tipo de coisa que faz o
 * paciente bloquear o WhatsApp da clínica.
 *
 * O horário anunciado é o MAIS CEDO: é a hora em que a pessoa precisa chegar.
 * Anunciar o mais tarde faria o paciente perder o primeiro atendimento.
 *
 * @param consultas  a lista completa lida do PDF
 * @param marcados   Set com os ÍNDICES marcados na tela
 */
export function agruparPorPessoa(consultas, marcados) {
  const porTelefone = new Map();

  consultas.forEach((consulta, indice) => {
    if (!marcados.has(indice)) return;
    // Sem celular não há para onde enviar. A tela já desabilita o checkbox;
    // esta guarda existe para o caso de a marcação vir de outro caminho.
    if (!consulta.celular) return;

    const atual = porTelefone.get(consulta.celular);
    if (!atual) {
      porTelefone.set(consulta.celular, {
        telefone: consulta.celular,
        nomeCompleto: consulta.paciente,
        horario: consulta.horario,
        consultas: [consulta],
      });
      return;
    }

    atual.consultas.push(consulta);
    // Strings "HH:MM" com zero à esquerda comparam certo em ordem alfabética.
    if (consulta.horario && consulta.horario < atual.horario) {
      atual.horario = consulta.horario;
    }
  });

  // A ordem de inserção do Map preserva a ordem do PDF, que é cronológica —
  // então o envio segue a fila do dia, e não uma ordem arbitrária.
  return [...porTelefone.values()];
}

/** Alternação com as chaves conhecidas. Montada das CONSTANTES, nunca do texto. */
const PADRAO_VARIAVEIS = new RegExp(
  VARIAVEIS.map(({ chave }) => chave.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'),
  'g',
);

/**
 * Troca as variáveis pelo valor da pessoa, numa PASSADA SÓ.
 *
 * A passada única não é detalhe de desempenho: substituindo uma variável de
 * cada vez, um valor já inserido volta a ser lido pela troca seguinte. Um
 * paciente cujo nome contenha "{dia}" teria a data enfiada no meio do próprio
 * nome — e os nomes vêm de um PDF de fora do sistema. Com `replace` e uma
 * função, o que foi substituído não é reescaneado.
 */
export function montarTexto(modelo, pessoa, diaIso) {
  const valores = {
    '{nome}': primeiroNome(pessoa?.nomeCompleto),
    '{nome_completo}': nomeApresentavel(pessoa?.nomeCompleto),
    '{dia}': formatarDia(diaIso),
    '{horario}': pessoa?.horario ?? '',
    '{telefone}': pessoa?.telefone ?? '',
  };

  return String(modelo ?? '').replace(PADRAO_VARIAVEIS, (chave) => valores[chave] ?? '');
}

/** Pessoa de exemplo para a prévia, quando ainda não há lista carregada. */
export const PESSOA_EXEMPLO = {
  telefone: '+5562983125801',
  nomeCompleto: 'MARIA APARECIDA DA SILVA',
  horario: '09:00',
  consultas: [],
};
